/**
 * Calendar export for generated day plans.
 *
 * Two outputs, both pure:
 *  - `buildPlanIcs` produces an RFC 5545 VCALENDAR with one VEVENT per stop,
 *    anchored to the plan's visit date in the precinct timezone. This is what
 *    "Add to calendar" downloads (Apple Calendar, Outlook, Google import).
 *  - `googleCalendarUrl` builds a Google Calendar "template" link for the whole
 *    day as a single event, with the itinerary in the details — the most
 *    reliable one-click "share to Google Calendar" UX for a multi-stop plan.
 *
 * Both carry an optional `planUrl` so the calendar entry links back to the
 * visitor's saved plan.
 *
 * No I/O happens here; the caller turns the ics string into a Blob/download.
 */
import { DateTime } from "luxon";

import type { DayPlan, PlannedStop } from "./types";

/** The V&A precinct's civil timezone. */
export const PRECINCT_ZONE = "Africa/Johannesburg";

export interface PlanCalendarOptions {
  /** Absolute URL of the saved plan, linked from every calendar entry. */
  planUrl?: string | null;
}

function planLinkLine(planUrl: string | null | undefined): string | null {
  return planUrl ? `Your plan: ${planUrl}` : null;
}

export interface CalendarEvent {
  uid: string;
  title: string;
  start: DateTime;
  end: DateTime;
  location: string;
  description: string;
  url: string | null;
}

/**
 * Turn the ordered stops into timed calendar events. Each stop runs for its
 * dwell time starting at its arrival time on the plan's visit date. Stops
 * without a parseable arrival time are skipped (defensive — the engine always
 * sets one).
 */
export function planStopsToEvents(
  plan: DayPlan,
  stops: PlannedStop[],
  { planUrl }: PlanCalendarOptions = {},
): CalendarEvent[] {
  const baseDate = plan.inputs.date; // ISO YYYY-MM-DD

  return stops.flatMap((stop, index) => {
    const start = DateTime.fromISO(`${baseDate}T${stop.arrivalTime}`, {
      zone: PRECINCT_ZONE,
    });
    if (!start.isValid) return [];

    const end = start.plus({ minutes: stop.dwellMinutes });
    const descriptionLines = [
      `${stop.place.district} · ${stop.dwellMinutes} min`,
      stop.place.bookingUrl ? `Book: ${stop.place.bookingUrl}` : null,
      planLinkLine(planUrl),
    ].filter((line): line is string => line !== null);

    return [
      {
        uid: `${plan.id ?? "plan"}-${stop.place.id}-${index}@vawaterfront`,
        title: stop.place.name,
        start,
        end,
        location: `${stop.place.name}, V&A Waterfront, Cape Town`,
        description: descriptionLines.join("\n"),
        url: planUrl ?? null,
      },
    ];
  });
}

/** Escape a value for inclusion in an ics text field (RFC 5545 §3.3.11). */
function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

function toIcsLocal(dt: DateTime): string {
  return dt.toFormat("yyyyLLdd'T'HHmmss");
}

/**
 * Build a downloadable VCALENDAR string for the plan. One VEVENT per stop,
 * floating local time tagged with the precinct timezone so calendars place it
 * correctly regardless of the visitor's own timezone.
 */
export function buildPlanIcs(
  plan: DayPlan,
  stops: PlannedStop[],
  options: PlanCalendarOptions = {},
): string {
  const events = planStopsToEvents(plan, stops, options);
  const stamp = DateTime.fromISO(plan.createdAt).toUTC();
  const dtstamp = (stamp.isValid ? stamp : DateTime.utc()).toFormat(
    "yyyyLLdd'T'HHmmss'Z'",
  );

  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//V&A Waterfront//Day Planner//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
  ];

  for (const event of events) {
    lines.push(
      "BEGIN:VEVENT",
      `UID:${event.uid}`,
      `DTSTAMP:${dtstamp}`,
      `DTSTART;TZID=${PRECINCT_ZONE}:${toIcsLocal(event.start)}`,
      `DTEND;TZID=${PRECINCT_ZONE}:${toIcsLocal(event.end)}`,
      `SUMMARY:${escapeIcsText(event.title)}`,
      `LOCATION:${escapeIcsText(event.location)}`,
      `DESCRIPTION:${escapeIcsText(event.description)}`,
      ...(event.url ? [`URL:${event.url}`] : []),
      "END:VEVENT",
    );
  }

  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}

/**
 * Build a Google Calendar "create event" link covering the whole day as a
 * single entry, with the full itinerary in the details. Google's template URL
 * only models one event, so a day overview is the right shape here; the .ics
 * download carries the per-stop breakdown for those who want it.
 */
export function googleCalendarUrl(
  plan: DayPlan,
  stops: PlannedStop[],
  { planUrl }: PlanCalendarOptions = {},
): string | null {
  const events = planStopsToEvents(plan, stops);
  if (events.length === 0) return null;

  const first = events[0]!;
  const last = events[events.length - 1]!;

  const fmt = (dt: DateTime) => dt.toUTC().toFormat("yyyyLLdd'T'HHmmss'Z'");

  const itinerary = stops.map(
    (stop) =>
      `${stop.arrivalTime}  ${stop.place.name} (${stop.place.district})`,
  );
  const planLink = planLinkLine(planUrl);
  const details = (planLink ? [...itinerary, "", planLink] : itinerary).join(
    "\n",
  );

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: "My day at the V&A Waterfront",
    dates: `${fmt(first.start)}/${fmt(last.end)}`,
    details,
    location: "V&A Waterfront, Cape Town",
    ctz: PRECINCT_ZONE,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
